// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import Vue from 'vue';
import Vuex from 'vuex';
import VuexI18n from 'vuex-i18n';
import BootstrapVue from 'bootstrap-vue';

import VueApollo from 'vue-apollo';
import { ApolloError } from 'apollo-client';
import SubscriptionHandler from '@/helpers/subscriptionHandler';
import processGraphQlError from '@/helpers/graphQlErrorHandler';

import './theme.scss';
import AppIndex from './AppIndex.vue';
import router from '@/router';
import { createApolloClient } from '@/apolloClient';

import { LightStateMachinePlugin } from '@/plugins/LightStateMachine';
import {
  LIGHT_STATE_MACHINE_CACHE_IS_OVER_UPDATE_FOR,
  LIGHT_STATE_MACHINE_CLEAR_PERIOD,
} from '@/constants/config';

Vue.use(BootstrapVue);
Vue.use(Vuex);

import VueAWN from 'vue-awesome-notifications';

const animationDuration = (process.env.WIZ_ENV != 'test' && process.env.WIZ_ENV != 'local-test')
  ? 300
  : 50;
const durationGlobal = (process.env.WIZ_ENV != 'test' && process.env.WIZ_ENV != 'local-test')
  ? 4000
  : undefined;
const durationSuccess = (process.env.WIZ_ENV != 'test' && process.env.WIZ_ENV != 'local-test')
  ? 2000
  : undefined;
Vue.use(VueAWN, {
  icons: { enabled: false },
  labels: { alert: '', success: '', info: '' },
  animationDuration,
  durations: {
    global: durationGlobal,
    success: durationSuccess,
  },
});

import createStore from '@/store';
import { User } from '@/store/modules/user.js';

const store = createStore();

Vue.use(VuexI18n.plugin, store);
localeInit();

Vue.config.productionTip = false;
Vue.use(VueApollo);

// Enable visibility observation for components
import VueObserveVisibility from 'vue-observe-visibility';
Vue.use(VueObserveVisibility);

const API_ENDPOINT = process.env.API_ENDPOINT;
const WS_ENDPOINT = process.env.WS_ENDPOINT;

const vClickOutside = require('v-click-outside');
Vue.use(vClickOutside);

// registering global components,
// TODO: check how to do it with webpack and require.context
import Spinner from '@/components/partials/spinner/Spinner.vue';
Vue.component('Spinner', Spinner);
import BigSpinner from '@/components/partials/bigSpinner/BigSpinner.vue';
Vue.component('BigSpinner', BigSpinner);
import InlineSpinner from '@/components/partials/inlineSpinner/InlineSpinner.vue';
Vue.component('InlineSpinner', InlineSpinner);
import CenteredIcon from '@/components/partials/centeredIcon/CenteredIcon.vue';
Vue.component('CenteredIcon', CenteredIcon);
import ButtonLink from '@/components/partials/buttonLink/ButtonLink.vue';
Vue.component('ButtonLink', ButtonLink);
import Divider from '@/components/partials/divider/Divider.vue';
Vue.component('Divider', Divider);

Vue.filter('formatPercent', (value: number) => {
  return `${
    Math.round(value)
  }%`;
});
Vue.filter('formatTemperature', (value: number) => {
  return `${
    Math.round(value)
  }K`;
});
Vue.filter('formatSeconds', (value: number) => {
  if (value < 5) { // Less than 5 seconds
    return Vue.i18n.translate('General_SecondsMask', {
      seconds: value.toFixed(1),
    })!;
  } else if (value < 60) { // Less than 1 minute
    return Vue.i18n.translate('General_SecondsMask', {
      seconds: Math.round(value),
    })!;
  } else {
    const minutes = Math.floor(value / 60);
    const seconds = value % 60;
    return Vue.i18n.translate('General_MinutesAndSecondsMask', {
      minutes,
      seconds,
    })!;
  }
});

import VueKonva from 'vue-konva';
import { localeInit } from "@/interfaces/Locale";
Vue.use(VueKonva);

import { extend } from 'vee-validate';
import { integer, min_value } from 'vee-validate/dist/rules';
extend('integer', { ...integer, message: Vue.i18n.translate('FormValidate_Integer') });
extend('min_value', { ...min_value, message: (_field, param) => Vue.i18n.translate('FormValidate_MinValue', { value: param.min })! });

export const eventBus = new Vue();

/* eslint-disable no-new */
(async () => {
  const routerVal = await router(store);

  routerVal.onError((error: Error) => {
    // With this we handle case when chunk can't be loaded because of
    // update process or something else
    if (/loading chunk \d* failed./i.test(error.message)) {
      setTimeout(() => {
        window.location.reload(true);
      }, 2000);
    }
  });
  // create Apollo provider
  const apolloClient = await createApolloClient(
    API_ENDPOINT!,
    WS_ENDPOINT!,
    () => {
      const organizationId = (store.state.user.currentUser as User).organizationId || undefined;
      return organizationId != undefined
        ? {
          organizationId,
        }
        : {};
    },
    async (error: ApolloError) => {
      await processGraphQlError(error, store, apolloClient, routerVal);
    },
  );
  const apolloProvider = new VueApollo({
    defaultClient: apolloClient,
    defaultOptions: {
      $query: {
        loadingKey: 'loading',
        fetchPolicy: 'network-only',
        error: (error) => {
          if (error.message.match('Unauthorized. Session is expired')) {
            // error msg may be shown twice due to apollo client retry mechanism
            console.log(error.message);
            return false;
          }
          return true;
        },
      },
    },
  });
  new SubscriptionHandler(store, apolloClient);

  // Why we need `apolloClient` here is described in the definition
  // of `LightStateMachinePluginOptions` interface
  Vue.use(
    LightStateMachinePlugin,
    {
      apolloClient,
      clearingPeriod: LIGHT_STATE_MACHINE_CLEAR_PERIOD,
      cacheIsOverUpdatesFor: LIGHT_STATE_MACHINE_CACHE_IS_OVER_UPDATE_FOR,
    },
  );

  new Vue({
    store,
    el: '#app',
    router: routerVal,
    apolloProvider,
    components: {
      AppIndex,
    },
    template: '<AppIndex />',
  });
})();
